import React, { useState, useEffect } from 'react';
import api, { vehicleAPI } from '../../services/api';
import { Link } from 'react-router-dom';
import PageTitle from '../shared/PageTitle';
import { useSettings } from '../../contexts/SettingsContext';
import { getCloseIconUrl } from '../../utils/closeIconUrl';

const Vehicles = () => {
  const { settings } = useSettings();
  const closeIconColor = settings.block_title_color || '#8B4513';
  const closeIconUrl = getCloseIconUrl(closeIconColor);

  const [vehicles, setVehicles] = useState([]);
  const [villas, setVillas] = useState([]);
  const [form, setForm] = useState({
    vehicle_type: '2 wheeler',
    villa_id: '',
    registration_number: '',
    make: '',
    model: '',
    color: ''
  });
  const [editingId, setEditingId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    loadVehicles();
    api.get('/api/villas/owner')
      .then(res => {
        if (Array.isArray(res.data) && res.data.length > 0) {
          setVillas(res.data);
          setForm(prev => ({ ...prev, villa_id: res.data[0].id }));
        }
      })
      .catch(err => console.error('Failed to load villas:', err));
  }, []);

  const loadVehicles = async () => {
    try {
      const res = await vehicleAPI.getAllVehicles();
      setVehicles(res.data);
    } catch (err) {
      console.error('Failed to load vehicles:', err);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!form.registration_number || !form.registration_number.trim()) {
      errors.registration_number = 'Registration number is required';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors(prev => {
        const copy = { ...prev };
        delete copy[name];
        return copy;
      });
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      if (editingId) {
        await vehicleAPI.updateVehicle(editingId, form);
      } else {
        await vehicleAPI.createVehicle(form);
      }
      setShowModal(false);
      setEditingId(null);
      setForm({ vehicle_type: '2 wheeler', villa_id: '', registration_number: '', make: '', model: '', color: '' });
      loadVehicles();
      setFormErrors({});
    } catch (err) {
      console.error('Save failed:', err);
    }
  };

  const handleEdit = v => {
    setFormErrors({});
    setEditingId(v.id);
    setForm({
      vehicle_type: v.vehicle_type,
      villa_id: v.villa_id,
      registration_number: v.registration_number,
      make: v.make || '',
      model: v.model || '',
      color: v.color || ''
    });
    setShowModal(true);
  };

  const handleDelete = async id => {
    if (!window.confirm('Are you sure you want to delete this vehicle?')) return;
    try {
      await vehicleAPI.deleteVehicle(id);
      loadVehicles();
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  return (
    <div className="container mt-4">
      <PageTitle
        icon="fa-car"
        title="My Vehicles"
        action={
          <div className='d-flex flex-wrap gap-2 align-items-center'>
            <Link to="/owner/dashboard" className="btn btn-outline-primary">
              <i className="fas fa-arrow-left me-2"></i>Back to Dashboard
            </Link>
            <button
              className="btn btn-primary"
              onClick={() => {
                setShowModal(true);
                setFormErrors({});
                setEditingId(null);
                setForm({ vehicle_type: '2 wheeler', villa_id: villas[0]?.id || '', registration_number: '', make: '', model: '', color: '' });
              }}
              disabled={villas.length === 0}
            >
              <i className="fas fa-plus me-2"></i>New Vehicle
            </button>
          </div>
        }
      />
      <p className="text-muted mb-4">View and manage your vehicles</p>

      {villas.length === 0 && (
        <div className="alert alert-warning">
          <i className="fas fa-exclamation-triangle me-2"></i>
          You need to have a villa assigned to your account before you can add vehicles.
        </div>
      )}

      {/* Modal for add/edit vehicle */}
      {showModal && (
        <>
          <div className="modal fade show" style={{ display: 'block', zIndex: 1080 }} tabIndex="-1">
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header" style={{ backgroundColor: settings.block_title_bg_color || '#f8f9fa', color: settings.block_title_color || '#8B4513' }}>
                  <h5 className="modal-title">{editingId ? 'Edit Vehicle' : 'Add Vehicle'}</h5>
                  <button type="button" className="btn-close" onClick={() => setShowModal(false)}
                  style={{ backgroundImage: closeIconUrl, fontSize: '18px', opacity: '0.9' }}
                    ></button>
                </div>
                <form onSubmit={handleSubmit}>
                  <div className="modal-body">
                    <div className="row">
                      <div className="col-md-4 mb-2">
                        <label>Vehicle Type</label>
                        <select className="form-select" name="vehicle_type" value={form.vehicle_type} onChange={handleChange}>
                          <option value="2 wheeler">2 wheeler</option>
                          <option value="4 wheeler">4 wheeler</option>
                        </select>
                      </div>
                      <div className="col-md-4 mb-2">
                        <label>Registration #</label>
                        <input
                          className={`form-control ${formErrors.registration_number ? 'is-invalid' : ''}`}
                          name="registration_number"
                          value={form.registration_number}
                          onChange={handleChange}
                          required
                        />
                        {formErrors.registration_number && (
                          <div className="invalid-feedback">
                            {formErrors.registration_number}
                          </div>
                        )}
                      </div>
                      <div className="col-md-4 mb-2">
                        <label>Make</label>
                        <input className="form-control" name="make" value={form.make} onChange={handleChange} />
                      </div>
                      <div className="col-md-4 mb-2">
                        <label>Model</label>
                        <input className="form-control" name="model" value={form.model} onChange={handleChange} />
                      </div>
                      <div className="col-md-4 mb-2">
                        <label>Color</label>
                        <input className="form-control" name="color" value={form.color} onChange={handleChange} />
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                    <button type="submit" className="btn btn-primary">{editingId ? 'Update Vehicle' : 'Create Vehicle'}</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}

      {vehicles.length === 0 ? (
            <div className="alert alert-info">
              <i className="fas fa-info-circle me-2"></i>
              No vehicles found.
            </div>
          ) : (
            <div className="table-responsive">
            <table className="table table-striped">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Type</th>
                  <th>Villa #</th>
                  <th>Registration #</th>
                  <th>Make</th>
                  <th>Model</th>
                  <th>Color</th>
                  <th>Created At</th>
                  <th>Updated At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                  {vehicles.map(v => (
                    <tr key={v.id}>
                      <td>{v.id}</td>
                      <td>{v.vehicle_type}</td>
                      <td>{v.villa_number}</td>
                      <td>{v.registration_number}</td>
                      <td>{v.make}</td>
                      <td>{v.model}</td>
                      <td>{v.color}</td>
                      <td>{new Date(v.created_at).toLocaleString()}</td>
                      <td>{new Date(v.updated_at).toLocaleString()}</td>
                      <td>
                        <button className="btn btn-sm btn-secondary me-1" onClick={() => { handleEdit(v); setShowModal(true); }}>
                          Edit
                        </button>
                        <button className="btn btn-sm btn-danger" onClick={() => handleDelete(v.id)}>
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          )}
    </div>
  );
};

export default Vehicles; 